import {
  ApolloClient,
  InMemoryCache,
  split,
  ApolloLink,
  Observable,
  type NormalizedCacheObject,
  type Operation,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, removeDirectivesFromDocument } from '@apollo/client/utilities';
import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import { Socket as PhoenixSocket } from 'phoenix';
import { getAppPrefix, isGatewayServed } from '@matrix-forge/ui/utils/appPrefix';
import { createLink } from 'apollo-absinthe-upload-link';
import { tracingLink, injectTraceHeaders } from './tracing';
import { getCsrfToken } from './csrf';
import { getActiveRoleHeader } from './activeRoleHeader';
import { getWindowToken } from './windowToken';
// import * as Sentry from '@sentry/react';

// Check if we're running in localhost/development mode
export function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.host;
  return host.includes('localhost') || host.includes('127.0.0.1');
}

// Builds a return URL for auth redirects.
// In dev mode (localhost), we need the full URL because Vite runs on a different port.
// In production, we use relative paths to pass validate_redirect_url/1 security checks.
export function buildReturnUrl(path: string, hash: string): string {
  if (isLocalhost()) {
    return window.location.origin + path + hash;
  }
  return path + hash;
}

// Token refresh response from /auth/refresh endpoint
export interface TokenRefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string | null;
}

// Callback for when tokens are refreshed
let onTokenRefresh: ((response: TokenRefreshResponse) => void) | null = null;

/**
 * Register a callback to be notified when tokens are refreshed.
 * Used by PrivateApolloProvider to update the JWT for WebSocket connections.
 */
export function setOnTokenRefresh(callback: ((response: TokenRefreshResponse) => void) | null) {
  onTokenRefresh = callback;
}

// Track if a refresh is in progress to prevent concurrent refresh calls
let isRefreshing = false;
let refreshPromise: Promise<TokenRefreshResponse | null> | null = null;

// Track whether this page load ever had a valid authenticated session.
// The session-expired veil is only meaningful for users who *were* logged in
// and then had their session expire — it should NOT show during the initial
// login redirect when there's no cookie yet. Flipped true by
// markSessionAuthenticated() (called from PrivateApolloProvider when
// userInfo loads) or by a successful silent refresh.
// PERSISTED, not just in-memory. As module state this reset to false on every
// page load, so the single most common expiry case — leave the tab, come back,
// reload — hit `promptLoginAgain()`'s `if (!hasEverBeenAuthenticated) return;`
// and bailed. The user got a BLANK PAGE: no veil, no redirect, no error, body
// text literally the launcher orb and nothing else.
//
// sessionStorage (not localStorage) keeps the original intent: "this TAB has
// held a session", so a genuinely first-time visitor still gets the LoginPage
// redirect rather than a veil stacked on top of it, while a reload inside an
// established tab correctly shows the expiry prompt.
const EVER_AUTHED_KEY = 'studio:session-was-authenticated';

let hasEverBeenAuthenticatedMemo = false;

function readEverAuthed(): boolean {
  if (hasEverBeenAuthenticatedMemo) return true;
  try {
    return window.sessionStorage.getItem(EVER_AUTHED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markSessionAuthenticated() {
  hasEverBeenAuthenticatedMemo = true;
  try {
    window.sessionStorage.setItem(EVER_AUTHED_KEY, '1');
  } catch {
    /* storage blocked — in-memory memo still covers this document */
  }
}

// Gateway front-door detection and the app prefix it implies.
//
// The implementation lives in `@matrix-forge/ui/utils/appPrefix` because the
// shared chat components build Studio-router URLs too (see
// `@matrix-forge/ui/utils/artifactUrl`) and `packages/ui` cannot import from
// the app that consumes it. Re-exported here so every existing importer, and
// `buildAppUrl` below, keeps its single call site.
export { getAppPrefix, isGatewayServed };

// Computes backend base URL derived from current window location
// In development (Vite dev server on port 5173), we return empty string
// to use relative URLs that go through Vite's proxy, enabling same-origin
// cookie handling for session-based auth.
export function getBackendUrl(): string {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const host = window.location.host;

    // Use relative URLs whenever possible so requests go through the same
    // origin — whether that's the Vite dev server (5173), the gateway (5000),
    // or direct Phoenix access (4000). This ensures cookies are same-origin.
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return '';
    }

    return `${protocol}//${host}`;
  }

  // Fallback for SSR or other contexts (development)
  return 'http://localhost:4000';
}

// Composes a same-origin URL for an app-owned path, carrying the gateway app
// prefix when (and only when) this SPA was served through the gateway.
//
// This is the ONE way to build a non-GraphQL backend URL. `getAppPrefix()`
// returns '' for every direct-access mode (production ingress, Vite :5173,
// Phoenix :4000), so `buildAppUrl('/auth/refresh')` is a literal no-op there —
// it still yields `/auth/refresh`. Behind the gateway front-door the same call
// yields `/studio/auth/refresh`, which the gateway routes via
// `DynamicDispatch.dispatch_prefixed_auth/2` (see
// `matrix_gateway/lib/matrix_gateway/dynamic_dispatch.ex`).
//
// Hard-coding an absolute `/auth/...` also reaches the gateway (it mounts the
// same `MatrixGateway.Auth.Plug` at the top level via `forward("/auth", …)`,
// and both shapes share the one `_matrix_gateway` cookie) — verified live:
// POST /auth/refresh and POST /studio/auth/refresh both return 200. So this is
// consistency, not a bug fix, for refresh.
//
// It DOES matter for routes that redirect. `dispatch_prefixed_auth/2` assigns
// `:gateway_app_prefix`, so the prefixed entry emits callback and post-login
// redirect targets carrying the app prefix — which is what the gateway itself
// redirects to (`spec.prefix <> "/auth/login"` in `require_gateway_login/2`).
// Building every app-owned URL the same way keeps login, refresh and the API
// on one shape instead of three.
export function buildAppUrl(path: string): string {
  return `${getBackendUrl()}${getAppPrefix()}${path}`;
}

// Computes HTTP endpoint derived from current window location
// Supports multi-endpoint routing based on endpoint name
//
// Exported as the ONE way to build a GraphQL HTTP URL: components that fetch()
// directly (log explorer, workflow debugger/tools) must use this instead of
// hard-coding '/api/v1alpha', which 404s behind the gateway front-door where
// the API lives under the app prefix (`/studio/api/v1alpha`).
export function getHttpEndpoint(endpointName?: string): string {
  // Map endpoint names to their API paths
  const endpointMap: Record<string, string> = {
    studio: '/api/v1alpha', // Studio operations (conversations, agents, workflows)
    asoc: '/api/asoc_v1alpha', // Asoc v1 case operations
    asoc_v2: '/api/v2alpha', // Asoc v2 case operations
  };

  // Default to studio endpoint if no endpoint specified
  const path = endpointName ? endpointMap[endpointName] || endpointMap.studio : endpointMap.studio;

  return buildAppUrl(path);
}

// Helper function to extract endpoint name from GraphQL operation
function getEndpointFromOperation(operation: Operation): string | undefined {
  // Check if endpoint is already set in context
  const context = operation.getContext();
  if (context.endpoint) {
    return context.endpoint;
  }

  // Extract @endpoint directive from the operation's AST
  const definition = operation.query.definitions.find(
    (def: any): def is any => def.kind === 'OperationDefinition'
  );

  if (!definition || !definition.directives) {
    return undefined;
  }

  const endpointDirective = definition.directives.find((dir: any) => dir.name.value === 'endpoint');

  if (!endpointDirective) {
    return undefined;
  }

  const nameArg = endpointDirective.arguments?.find((arg: any) => arg.name.value === 'name');

  return nameArg?.value?.value;
}

// Link that strips @endpoint directive from queries before sending to server
// The @endpoint directive is client-side only for routing purposes
const removeEndpointDirectiveLink = new ApolloLink((operation: Operation, forward: any) => {
  // Extract endpoint name before removing the directive
  const endpointName = getEndpointFromOperation(operation);

  // Remove @endpoint directive from the query
  const cleanedQuery = removeDirectivesFromDocument(
    [{ name: 'endpoint', remove: true }],
    operation.query
  );

  if (cleanedQuery) {
    operation.query = cleanedQuery;
  }

  // Store endpoint name in context for routing
  operation.setContext({
    ...operation.getContext(),
    endpoint: endpointName,
  });

  return forward(operation);
});

// Link that adds credentials to all HTTP requests for session-based auth, plus
// the active role the user is operating as (role switcher). The backend narrows
// authorization to this role when it's one the user holds; omitting it means
// "operate as all my roles" (pre-switch behaviour).
const credentialsLink = setContext((_, { headers }) => {
  const activeRole = getActiveRoleHeader();

  // Tenant Window Manager: a studio instance embedded in a workspace window
  // authenticates with its OWN minted `wtoken` (scoped to that window's tenant
  // + assumed role), NOT the shared session cookie. We MUST omit the cookie —
  // otherwise the backend's FetchSession resolves the cookie first and
  // BearerAuth never runs, so the window would silently show the shell's live
  // tenant instead of its own (the isolation bug this fixes). No active-role
  // header either: the role is baked into the token's claims server-side.
  const windowToken = getWindowToken();
  if (windowToken) {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${windowToken}`,
      },
      credentials: 'omit',
    };
  }

  return {
    headers: {
      ...headers,
      ...(activeRole ? { 'X-Matrix-Active-Role': activeRole } : {}),
    },
    credentials: 'include', // Send cookies with requests for session-based auth
  };
});

// Returns a new Absinthe upload link instance with multi-endpoint routing
// This supports file uploads to Absinthe GraphQL backends and routes based on @endpoint directive
const makeHttpLink = () => {
  // Create separate links for each endpoint
  const studioLink = createLink({ uri: getHttpEndpoint('studio') });
  const asocLink = createLink({ uri: getHttpEndpoint('asoc') });
  const asocV2Link = createLink({ uri: getHttpEndpoint('asoc_v2') });

  // Route based on endpoint stored in context (after @endpoint directive is stripped)
  const routingLink = ApolloLink.split(
    (operation: Operation) => operation.getContext().endpoint === 'asoc_v2',
    asocV2Link,
    ApolloLink.split(
      (operation: Operation) => operation.getContext().endpoint === 'asoc',
      asocLink,
      studioLink // default to studio endpoint
    )
  );

  // Chain: add credentials → strip @endpoint directive → route to correct endpoint
  return ApolloLink.from([credentialsLink, removeEndpointDirectiveLink, routingLink]);
};

// Returns a new Apollo Client instance which uses an HTTP link for all operations.
// Includes authErrorLink so that session/auth failures (401 / UNAUTHENTICATED) on
// the public client also trigger the silent refresh + Keycloak login redirect flow
// instead of failing silently (e.g. during session restore or background feature polls).
export const makePublicClient = () => {
  return new ApolloClient({
    link: ApolloLink.from([authErrorLink, makeHttpLink()]),
    cache: new InMemoryCache({
      typePolicies: {
        NodeExecution: {
          keyFields: ['nodeId'],
        },
        Document: {
          keyFields: false,
        },
      },
    }),
  });
};

// NOTE: `publicClient` is instantiated below, after `authErrorLink` is defined,
// because makePublicClient() captures authErrorLink lexically.

/**
 * Lightweight session keepalive — calls /auth/refresh to extend the session
 * cookie WITHOUT notifying onTokenRefresh. This avoids recreating the Apollo
 * client and WebSocket, which would interrupt active subscriptions.
 *
 * Use this when the goal is just to prevent session expiry (e.g. during
 * long-running agent builds) rather than to update the JWT used by Apollo.
 */
export async function keepSessionAlive(): Promise<boolean> {
  try {
    // buildAppUrl, not a hard-coded '/auth/refresh': behind the gateway the
    // refresh must carry the app prefix (`/studio/auth/refresh`). No-op for
    // direct access, where the prefix is ''.
    const response = await fetch(buildAppUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (response.ok) {
      console.log('🔄 Session keepalive: cookie refreshed');
      return true;
    } else {
      console.log('🔐 Session keepalive failed:', response.status);
      return false;
    }
  } catch (error) {
    console.log('🔐 Session keepalive error:', error);
    return false;
  }
}

/**
 * Attempts to silently refresh the session via /auth/refresh.
 * Returns the token response on success, or null on failure.
 * Uses a singleton pattern to prevent concurrent refresh calls.
 *
 * NOTE: This triggers onTokenRefresh which recreates the Apollo client and
 * WebSocket. For keepalive-only use cases, prefer keepSessionAlive() instead.
 */
export async function attemptSilentRefresh(): Promise<TokenRefreshResponse | null> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // Same gateway-prefix rule as keepSessionAlive() above.
      const response = await fetch(buildAppUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include', // Send cookies
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const data: TokenRefreshResponse = await response.json();
        console.log('🔄 Session refreshed successfully');
        markSessionAuthenticated();

        // Notify any registered callbacks (e.g., PrivateApolloProvider)
        if (onTokenRefresh) {
          onTokenRefresh(data);
        }

        return data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('🔐 Session refresh failed:', errorData.error || response.status);
        return null;
      }
    } catch (error) {
      console.log('🔐 Session refresh error:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function buildLoginUrl(): string {
  const returnUrl = buildReturnUrl(window.location.pathname, window.location.hash);
  // Prefixed for the same reason as /auth/refresh, and to match what the
  // gateway itself redirects to (`spec.prefix <> "/auth/login"` in
  // DynamicDispatch.require_gateway_login/2) and what UserInfoProvider already
  // builds. Direct access is unaffected (prefix is '').
  return `${buildAppUrl('/auth/login')}?redirect=${encodeURIComponent(returnUrl)}`;
}

/**
 * Prompts the user to log in again via a full-screen veil.
 * Unlike an automatic redirect, this preserves the current page state
 * (unsaved form inputs, scroll position) until the user acknowledges.
 *
 * Debounced via the singleton DOM node: if the veil is already mounted,
 * subsequent 401s are no-ops.
 */
const SESSION_EXPIRED_VEIL_ID = 'session-expired-veil';

function promptLoginAgain() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SESSION_EXPIRED_VEIL_ID)) return;
  // Only show the session-expired veil for users who were actually
  // authenticated at some point. During the initial login redirect (no
  // cookie yet), LoginPage handles navigation to Keycloak — the veil
  // would just stack on top of it.
  if (!readEverAuthed()) return;

  const veil = document.createElement('div');
  veil.id = SESSION_EXPIRED_VEIL_ID;
  veil.setAttribute('role', 'alertdialog');
  veil.setAttribute('aria-modal', 'true');
  veil.setAttribute('aria-labelledby', `${SESSION_EXPIRED_VEIL_ID}-title`);
  veil.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:rgba(15,23,42,0.78)',
    'backdrop-filter:blur(6px)',
    '-webkit-backdrop-filter:blur(6px)',
    'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
    'animation:session-expired-fade 200ms ease-out',
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'background:#ffffff',
    'color:#0f172a',
    'border-radius:12px',
    'padding:32px',
    'max-width:420px',
    'width:calc(100% - 32px)',
    'box-shadow:0 25px 50px -12px rgba(0,0,0,0.5)',
    'text-align:center',
  ].join(';');

  const title = document.createElement('h2');
  title.id = `${SESSION_EXPIRED_VEIL_ID}-title`;
  title.textContent = 'Your session has expired';
  title.style.cssText = 'margin:0 0 8px;font-size:20px;font-weight:600;';

  const desc = document.createElement('p');
  desc.textContent = 'Click below to log in again.';
  desc.style.cssText = 'margin:0 0 24px;font-size:14px;color:#475569;';

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Log in';
  button.style.cssText = [
    'background:#6366f1',
    'color:#ffffff',
    'border:none',
    'border-radius:8px',
    'padding:10px 24px',
    'font-size:14px',
    'font-weight:600',
    'cursor:pointer',
    'transition:background 150ms ease',
  ].join(';');
  button.addEventListener('mouseenter', () => { button.style.background = '#4f46e5'; });
  button.addEventListener('mouseleave', () => { button.style.background = '#6366f1'; });
  button.addEventListener('click', () => {
    window.location.href = buildLoginUrl();
  });

  if (!document.getElementById(`${SESSION_EXPIRED_VEIL_ID}-keyframes`)) {
    const style = document.createElement('style');
    style.id = `${SESSION_EXPIRED_VEIL_ID}-keyframes`;
    style.textContent = '@keyframes session-expired-fade{from{opacity:0}to{opacity:1}}';
    document.head.appendChild(style);
  }

  card.appendChild(title);
  card.appendChild(desc);
  card.appendChild(button);
  veil.appendChild(card);
  document.body.appendChild(veil);
  button.focus();
}

/**
 * Checks if an error indicates an authentication problem.
 */
function isAuthError(networkError: any, graphQLErrors: readonly any[] | undefined): boolean {
  // Check for 401 in network errors
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    return true;
  }

  // A 400 with NO GraphQL error payload is the gateway rejecting the request
  // outright — which is what an expired session actually produces. Matching only
  // 401 meant none of this machinery (silent refresh, Keycloak login redirect)
  // ever ran on the real expiry path: once the gateway JWT died, every operation
  // came back `400 Bad Request` with a plain-text body, `isAuthError` said no,
  // and the SPA rendered a BLANK PAGE — body text was literally the launcher orb
  // and nothing else. No redirect, no error, no retry.
  //
  // Narrow on purpose. A malformed query also yields 400, but Absinthe answers
  // that with a JSON body carrying `errors`, so `graphQLErrors` is populated and
  // we fall through — a genuine query bug must NOT bounce the user to login.
  // Transport-level rejection is exactly the case where `graphQLErrors` is empty.
  if (
    networkError &&
    'statusCode' in networkError &&
    networkError.statusCode === 400 &&
    (!graphQLErrors || graphQLErrors.length === 0)
  ) {
    return true;
  }

  // Check for UNAUTHENTICATED errors in GraphQL responses
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const message = err.message?.toLowerCase() || '';
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        message.includes('unauthenticated') ||
        message.includes('unauthorized') ||
        message.includes('not authenticated')
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Error link that handles authentication errors with retry logic.
 *
 * When a 401 or UNAUTHENTICATED error is received:
 * 1. Attempt to silently refresh the session via /auth/refresh
 * 2. If refresh succeeds, retry the original request
 * 3. If refresh fails (session truly expired), redirect to login
 *
 * This prevents the "redirect to home" problem during token refresh.
 */
const authErrorLink = onError(({ networkError, graphQLErrors, operation, forward }) => {
  if (!isAuthError(networkError, graphQLErrors)) {
    return; // Not an auth error, let it pass through
  }

  console.log('🔐 Auth error detected, attempting silent refresh...');

  // Return an Observable that handles the retry logic.
  //
  // Leak fix (matrix-p2lc): the inner `forward(operation).subscribe(...)` handle
  // is captured and released via the Observable's teardown, and a `cancelled`
  // flag guards the async continuation so nothing calls the observer after
  // Apollo tears the link down (e.g. component unmount mid-retry). This does NOT
  // cap or throttle retries — on a sustained 401 loop each attempt simply
  // releases its own subscription instead of pinning it forever.
  return new Observable((observer) => {
    let inner: { unsubscribe: () => void } | undefined;
    let cancelled = false;

    attemptSilentRefresh()
      .then((refreshResult) => {
        if (cancelled) return;

        if (refreshResult) {
          // Refresh succeeded - retry the original operation
          console.log('🔄 Retrying request after session refresh...');
          inner = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        } else {
          // Refresh failed - session is truly expired, prompt user to log in
          promptLoginAgain();
          observer.complete();
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('🔐 Unexpected error during refresh:', error);
        promptLoginAgain();
        observer.complete();
      });

    // Teardown: unsubscribe the retried operation so its context + closures are
    // released when Apollo disposes this link (unmount / abort). Keeps infinite
    // retry flat instead of accumulating a live subscription per 401.
    return () => {
      cancelled = true;
      inner?.unsubscribe();
    };
  });
});

// Returns a new ApolloLink instance which combines error handling, tracing,
// and standard HTTP link with multi-endpoint routing. Authentication is handled via session cookie.
const makeHttpLinkWithAuth = () => ApolloLink.from([authErrorLink, tracingLink, makeHttpLink()]);

// Instantiate the public client now that authErrorLink is defined. See
// makePublicClient for rationale — 401s on the public client (e.g. during
// session restore or feature polling) must also trigger the Keycloak redirect.
export const publicClient = makePublicClient();

// Get socket endpoint derived from current window location
// Supports multi-endpoint routing based on endpoint name
function getSocketEndpoint(endpointName?: string): string {
  // Map endpoint names to their WebSocket paths
  const endpointMap: Record<string, string> = {
    studio: '/v1alpha/graphql_socket',
    asoc: '/asoc_v1alpha/graphql_socket',
    asoc_v2: '/v2alpha/graphql_socket',
  };

  // Default to studio endpoint if no endpoint specified
  const socketPath = endpointName
    ? endpointMap[endpointName] || endpointMap.studio
    : endpointMap.studio;

  // In production/release, derive from current window location
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // Prefix WebSocket paths with the app prefix so the gateway
    // can route them. The gateway strips the prefix before proxying.
    const appPrefix = getAppPrefix();
    return `${protocol}//${host}${appPrefix}${socketPath}`;
  }

  // Fallback for SSR or other contexts
  return `ws://localhost:4000${socketPath}`;
}

// Sentinel that the backend writes onto `ctx.credentials.jwt` during
// impersonation (see MatrixAuth.Plugs.FetchSession.redact_credentials/1) so
// the parent operator's real JWT never reaches the impersonated client. The
// SPA still receives the string on `userInfo.jwt` for "logged in" checks,
// but it must not be sent as a socket token — the backend can't resolve it
// to a session, so the WS upgrade fails and subscriptions (e.g. persona
// chat responses) silently drop. Falling back to no token lets the
// session-cookie auth path on the backend (SocketAuth.authenticate_via_sid)
// take over with the impersonation overlay already applied.
const IMPERSONATION_PLACEHOLDER = 'impersonated';

// Creates an Absinthe socket for WebSocket subscriptions
// Token is passed in params since cookies aren't reliably forwarded during WS upgrades.
// When the token is the impersonation redaction placeholder, omit it so the backend
// falls back to the signed _matrix_sid session cookie for authentication.
// Token refresh is handled by PrivateApolloProvider recreating the client when JWT changes
const makeAbsintheSocket = (token?: string, endpointName?: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const endpoint = getSocketEndpoint(endpointName);

  // Get trace context headers for WebSocket connection
  const traceHeaders = (() => {
    try {
      const headers = injectTraceHeaders({});

      // Return only the trace headers we need for WebSocket params
      const params: Record<string, string> = {};
      if (headers.traceparent) params.traceparent = headers.traceparent;
      if (headers.tracestate) params.tracestate = headers.tracestate;

      return params;
    } catch (e) {
      return {};
    }
  })();

  // In a workspace window, the socket must authenticate with the window's own
  // minted token (SocketAuth resolves it via its iframe_session fallback),
  // NOT the operator's JWT — otherwise subscriptions would resolve to the
  // shell's live tenant, breaking window isolation. Falls through to the JWT
  // (or cookie-sid fallback) for normal top-level studio tabs.
  const windowToken = getWindowToken();
  const effectiveToken = windowToken ?? token;
  const usableToken =
    effectiveToken && effectiveToken !== IMPERSONATION_PLACEHOLDER ? effectiveToken : undefined;

  // V2 Alpha requires Bearer token in Authorization param
  // V1 Alpha uses 'token' param
  const authParams = usableToken
    ? endpointName === 'asoc_v2'
      ? { Authorization: `Bearer ${usableToken}` }
      : { token: usableToken }
    : {};

  // CSRF token from the `<meta name="csrf-token">` tag injected by
  // `MatrixStudio.Controllers.StaticHtmlHelper` on every SPA shell render.
  // Phoenix's WebSocket transport requires `_csrf_token` to populate
  // `connect_info.session` (otherwise `connect_session/4` returns nil and
  // `SocketAuth`'s sid fallback can't see the `_matrix_sid` key needed
  // for impersonated sessions). Without this the socket still works for
  // non-impersonated sessions (token-based auth bypasses the session
  // decode), so the symptom is narrow: missing assistant replies + error
  // toasts while operating in a child tenant. See Strike48/matrix#2620.
  const csrfToken = getCsrfToken();
  const csrfParams = csrfToken ? { _csrf_token: csrfToken } : {};

  const phoenixSocket = new PhoenixSocket(endpoint, {
    params: {
      ...traceHeaders,
      ...csrfParams,
      ...authParams,
    },
  });

  // Connect immediately
  phoenixSocket.connect();

  // Return BOTH the Absinthe wrapper (for the Apollo link) and the raw
  // PhoenixSocket. The raw socket is the only object exposing `.disconnect()`;
  // callers must hold it so a superseded client (rebuilt on JWT change) can
  // close its socket instead of leaving it reconnecting forever — the orphan
  // that leaks memory (matrix-p2lc). This does NOT change retry behaviour: the
  // live socket still reconnects infinitely; we just stop the discarded one.
  return { absintheSocket: AbsintheSocket.create(phoenixSocket), phoenixSocket };
};

const makeAbsintheSocketLink = (token?: string, endpointName?: string) => {
  const made = makeAbsintheSocket(token, endpointName);
  if (!made) return { link: undefined, socket: undefined };
  return { link: createAbsintheSocketLink(made.absintheSocket) as any, socket: made.phoenixSocket };
};

const makeSplitLink = (token?: string, endpointName?: string) => {
  const { link: socketLink, socket } = makeAbsintheSocketLink(token, endpointName);
  const httpLink = makeHttpLinkWithAuth();

  // If we can't create a socket link, just use HTTP for everything.
  if (!socketLink) {
    return { link: httpLink, socket: undefined };
  }

  const link = split(
    ({ query }: any) => {
      const definition = getMainDefinition(query);
      const isSubscription =
        definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

      // Always use WebSocket for subscriptions - they require real-time connection
      return isSubscription;
    },
    socketLink,
    httpLink
  );

  return { link, socket };
};

// An Apollo client paired with the raw PhoenixSocket backing its subscription
// link (or `undefined` for an HTTP-only link / SSR). Callers that rebuild the
// client on JWT change MUST call `socket?.disconnect()` on the OLD bundle so
// the superseded socket stops reconnecting (leak fix, matrix-p2lc).
export type PrivateClientBundle = {
  client: ApolloClient<NormalizedCacheObject>;
  socket?: PhoenixSocket;
};

// Returns a new Apollo Client (+ its PhoenixSocket handle) which uses a split
// link, meaning an HTTP link for queries and mutations, and an absinthe socket
// link for subscriptions. HTTP auth is via session cookie. WebSocket auth
// passes token in params. Uses the Studio (V1alpha) WebSocket endpoint.
export const makePrivateClient = (token?: string): PrivateClientBundle => {
  const { link, socket } = makeSplitLink(token, 'studio');

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache({
      typePolicies: {
        NodeExecution: {
          keyFields: ['nodeId'],
        },
        Document: {
          keyFields: false,
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });

  return { client, socket };
};

// Returns a new Apollo Client (+ its PhoenixSocket handle) specifically for V2
// Alpha subscriptions. Uses the V2alpha WebSocket endpoint
// (/v2alpha/graphql_socket). Required because Apollo Client can only use one
// WebSocket endpoint at a time.
export const makePrivateClientV2 = (token?: string): PrivateClientBundle => {
  const { link, socket } = makeSplitLink(token, 'asoc_v2');

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });

  return { client, socket };
};
